import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Modal } from 'antd';
import { toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaBuilding,
  FaExternalLinkAlt,
  FaIndustry,
} from 'react-icons/fa';
import {
  fetchPublishedInfrastructureById,
  fetchSellerListingInventoryDetail,
  patchSellerListingInventoryLine,
  patchSellerListingInventoryBlockDeactivate,
  postMyPublishedListingInventory,
  postSellerListingInventoryBlock,
  postSellerInventoryAllocationRelease,
  postSellerInventoryRenewal,
} from '../../services/infrastructureLeasingListing.service';
import { resolvePublicFileUrl } from '../../constants/api-urls';
import {
  formatBookedRangesSummaryLines,
  estimateEarliestCapacityAfterBookings,
  getInventoryLineSummaryBullets,
  getListingAvailabilityHeadline,
  inventoryTransactionScopeFromForm,
} from '../../utils/infrastructureListingInventoryAvailabilityUi';
import { filterAllocationsExpiringSoon } from '../../utils/infrastructureSellerInventoryAggregate';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_seller_inventory.scss';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'configure', label: 'Configure' },
  { id: 'planner', label: 'Availability planner' },
  { id: 'leases', label: 'Allocations & leases' },
  { id: 'renewals', label: 'Renewals & expiry' },
];

const KIND_OPTS = [
  { value: 'unit', label: 'Unit-based' },
  { value: 'area', label: 'Area-based' },
  { value: 'capacity', label: 'Capacity-based' },
  { value: 'time', label: 'Time-based' },
  { value: 'hybrid', label: 'Hybrid' },
];

function str(v) {
  return v == null ? '' : String(v).trim();
}

function isoFromSql(d) {
  if (d == null) return '';
  const s = String(d).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : String(d).slice(0, 10);
}

function invLabel(rows, inventoryId) {
  const row = (rows || []).find((r) => Number(r.infrastructure_listing_inventory_id) === Number(inventoryId));
  if (!row) return `Line #${inventoryId}`;
  const k = str(row.inventory_kind);
  const lb = str(row.label);
  return lb ? `${k} — ${lb}` : k;
}

export default function InfrastructureLeasingSellerInventoryHubPage() {
  const { listingId: listingIdParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabRaw = str(searchParams.get('tab')).toLowerCase();
  const tab = TABS.some((t) => t.id === tabRaw) ? tabRaw : 'overview';

  const listingId = Number(listingIdParam);
  const [listing, setListing] = useState(null);
  const [invPayload, setInvPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    inventoryKind: 'unit',
    label: '',
    totalUnits: '',
    totalArea: '',
    totalCapacity: '',
    areaUom: 'sqft',
    capacityUom: '',
    hybridNote: '',
  });

  const [blockOpen, setBlockOpen] = useState(false);
  const [blockInvId, setBlockInvId] = useState(null);
  const [blockForm, setBlockForm] = useState({
    blockKind: 'maintenance',
    blockStartDate: '',
    blockEndDate: '',
    notes: '',
    unitsReduction: '',
    areaReduction: '',
    capacityReduction: '',
  });

  const [releaseModal, setReleaseModal] = useState({ open: false, enquiryId: null });
  const [releaseReason, setReleaseReason] = useState('');
  const [renewModal, setRenewModal] = useState({ open: false, enquiryId: null, currentEnd: '' });
  const [renewDate, setRenewDate] = useState('');

  const setTab = (next) => {
    setSearchParams(next === 'overview' ? {} : { tab: next });
  };

  const loadListing = useCallback(async () => {
    if (!Number.isFinite(listingId) || listingId < 1) return;
    try {
      const res = await fetchPublishedInfrastructureById(listingId);
      if (res?.success && res.data) {
        setListing(res.data);
      } else {
        setListing(null);
        setLoadErr(res?.message || 'Listing not found.');
      }
    } catch {
      setListing(null);
      setLoadErr('Could not load listing.');
    }
  }, [listingId]);

  const loadInventory = useCallback(async () => {
    if (!Number.isFinite(listingId) || listingId < 1) return;
    setLoading(true);
    setLoadErr('');
    try {
      const res = await fetchSellerListingInventoryDetail(listingId);
      if (res?.success && res.data) {
        setInvPayload(res.data);
      } else {
        setInvPayload(null);
        setLoadErr(res?.message || 'Could not load inventory.');
      }
    } catch (e) {
      setInvPayload(null);
      setLoadErr(e?.response?.data?.message || e?.message || 'Could not load inventory.');
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    loadListing();
  }, [loadListing]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const items = invPayload?.items || [];
  const availability = invPayload?.availability || null;
  const allocations = invPayload?.allocations || [];
  const blocks = invPayload?.blocks || [];

  const txScopeDefault = useMemo(
    () => inventoryTransactionScopeFromForm(listing?.form?.transactionType),
    [listing],
  );

  const headline = useMemo(() => {
    if (!listing || !availability) return null;
    return getListingAvailabilityHeadline({ ...listing, inventoryAvailability: availability });
  }, [listing, availability]);

  const lineBullets = useMemo(() => {
    if (!listing || !availability) return [];
    return getInventoryLineSummaryBullets({ ...listing, inventoryAvailability: availability });
  }, [listing, availability]);

  const plannerRows = useMemo(() => {
    const rows = [];
    const booked = availability?.bookedRanges || [];
    booked.forEach((r) => {
      rows.push({
        kind: 'booked',
        start: r.start,
        end: r.end,
        label: 'Committed / pipeline window',
      });
    });
    blocks.forEach((b) => {
      rows.push({
        kind: 'block',
        start: isoFromSql(b.block_start_date),
        end: isoFromSql(b.block_end_date),
        label: `${str(b.block_kind)} · ${invLabel(items, b.infrastructure_listing_inventory_id)}`,
        notes: str(b.notes),
      });
    });
    rows.sort((a, b) => a.start.localeCompare(b.start));
    return rows;
  }, [availability, blocks, items]);

  const renewalsSoon = useMemo(() => filterAllocationsExpiringSoon(allocations, { withinDays: 90 }), [allocations]);

  const openEdit = (row) => {
    let hybridText = '';
    try {
      const h = row.hybrid_dimensions_json;
      if (h != null && typeof h === 'object') {
        hybridText = JSON.stringify(h, null, 2);
      } else if (h != null && typeof h === 'string') {
        hybridText = h;
      }
    } catch {
      hybridText = '';
    }
    setEditRow(row);
    setEditForm({
      label: row.label ?? '',
      totalUnits: row.total_units ?? '',
      totalArea: row.total_area ?? '',
      totalCapacity: row.total_capacity ?? '',
      areaUom: row.area_uom ?? 'sqft',
      capacityUom: row.capacity_uom ?? '',
      transactionScope: str(row.transaction_scope).toLowerCase() || 'lease',
      isDefault: Number(row.is_default) === 1,
      isActive: Number(row.is_active) === 1,
      hybridJson: hybridText,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const optNum = (raw, label) => {
      if (raw === '' || raw == null) return null;
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        toast.error(`${label} must be a valid number.`);
        return undefined;
      }
      return n;
    };
    const tu = optNum(editForm.totalUnits, 'Total units');
    const ta = optNum(editForm.totalArea, 'Total area');
    const tc = optNum(editForm.totalCapacity, 'Total capacity');
    if (tu === undefined || ta === undefined || tc === undefined) return;

    const body = {
      label: str(editForm.label) || null,
      totalUnits: tu,
      totalArea: ta,
      totalCapacity: tc,
      areaUom: str(editForm.areaUom) || 'sqft',
      capacityUom: str(editForm.capacityUom) || null,
      transactionScope: editForm.transactionScope === 'sale' || editForm.transactionScope === 'both' ? editForm.transactionScope : 'lease',
      isDefault: Boolean(editForm.isDefault),
      isActive: Boolean(editForm.isActive),
    };
    const kind = str(editRow.inventory_kind).toLowerCase();
    if (kind === 'hybrid' && str(editForm.hybridJson)) {
      try {
        body.hybridDimensions = JSON.parse(editForm.hybridJson);
      } catch {
        toast.error('Hybrid dimensions must be valid JSON.');
        return;
      }
    }
    try {
      const res = await patchSellerListingInventoryLine(listingId, editRow.infrastructure_listing_inventory_id, body);
      if (res?.success === false) {
        toast.error(res?.message || 'Update failed');
        return;
      }
      toast.success('Inventory line updated');
      setEditOpen(false);
      loadInventory();
      loadListing();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Update failed');
    }
  };

  const submitAddLine = async () => {
    const k = addForm.inventoryKind;
    const body = {
      inventoryKind: k,
      isDefault: true,
      label: str(addForm.label) || null,
      areaUom: str(addForm.areaUom) || 'sqft',
      transactionScope: txScopeDefault,
    };
    if (str(addForm.capacityUom)) body.capacityUom = str(addForm.capacityUom);
    if (k === 'unit') body.totalUnits = Number(addForm.totalUnits);
    else if (k === 'area' || k === 'time') body.totalArea = Number(addForm.totalArea);
    else if (k === 'capacity') {
      body.totalCapacity = Number(addForm.totalCapacity);
      body.capacityUom = str(addForm.capacityUom);
      if (!body.capacityUom) {
        toast.error('Capacity unit is required.');
        return;
      }
    } else if (k === 'hybrid') {
      const u = Number(addForm.totalUnits);
      const a = Number(addForm.totalArea);
      const c = Number(addForm.totalCapacity);
      if (Number.isFinite(u) && u > 0) body.totalUnits = u;
      if (Number.isFinite(a) && a > 0) body.totalArea = a;
      if (Number.isFinite(c) && c > 0) body.totalCapacity = c;
      body.hybridDimensions = {
        schema: 'seller_hub_v1',
        note: str(addForm.hybridNote) || undefined,
      };
      if (!body.totalUnits && !body.totalArea && !body.totalCapacity) {
        toast.error('Hybrid requires at least one positive total.');
        return;
      }
    }
    try {
      const res = await postMyPublishedListingInventory(listingId, body);
      if (res?.success === false) {
        toast.error(res?.message || 'Could not add line');
        return;
      }
      toast.success('Inventory line added');
      setAddOpen(false);
      loadInventory();
      loadListing();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not add line');
    }
  };

  const submitBlock = async () => {
    if (!blockInvId) return;
    try {
      const res = await postSellerListingInventoryBlock(listingId, blockInvId, {
        blockKind: blockForm.blockKind,
        blockStartDate: blockForm.blockStartDate,
        blockEndDate: blockForm.blockEndDate,
        notes: str(blockForm.notes) || null,
        unitsReduction: blockForm.unitsReduction === '' ? null : Number(blockForm.unitsReduction),
        areaReduction: blockForm.areaReduction === '' ? null : Number(blockForm.areaReduction),
        capacityReduction: blockForm.capacityReduction === '' ? null : Number(blockForm.capacityReduction),
      });
      if (res?.success === false) {
        toast.error(res?.message || 'Block failed');
        return;
      }
      toast.success('Maintenance / hold block added');
      setBlockOpen(false);
      loadInventory();
      loadListing();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Block failed');
    }
  };

  const deactivateBlock = async (blockId) => {
    try {
      const res = await patchSellerListingInventoryBlockDeactivate(listingId, blockId);
      if (res?.success === false) {
        toast.error(res?.message || 'Could not remove block');
        return;
      }
      toast.success('Block deactivated');
      loadInventory();
      loadListing();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not remove block');
    }
  };

  const doRelease = async () => {
    const eid = releaseModal.enquiryId;
    if (!eid) return;
    try {
      const res = await postSellerInventoryAllocationRelease(listingId, eid, {
        reason: str(releaseReason) || undefined,
      });
      if (res?.success === false) {
        toast.error(res?.message || 'Release failed');
        return;
      }
      if (res?.data?.unwound) {
        toast.success(res?.message || 'Pending deal cancelled and inventory released');
      } else if (res?.data?.coordinatedAcceptedCancel) {
        toast.success(res?.message || 'Accepted deal cancelled (inventory + agreement + enquiry)');
      } else if (res?.data?.coordinatedUnpaidCancel) {
        toast.success(res?.message || 'Unpaid deal cancelled (inventory + agreement + enquiry)');
      } else {
        toast.success(res?.message || 'Reserved allocation released');
      }
      setReleaseModal({ open: false, enquiryId: null });
      setReleaseReason('');
      loadInventory();
      loadListing();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Release failed');
    }
  };

  const doRenew = async () => {
    const eid = renewModal.enquiryId;
    if (!eid || !str(renewDate)) return;
    try {
      const res = await postSellerInventoryRenewal(listingId, eid, str(renewDate));
      if (res?.success === false) {
        toast.error(res?.message || 'Renewal failed');
        return;
      }
      toast.success('Renewal recorded');
      setRenewModal({ open: false, enquiryId: null, currentEnd: '' });
      setRenewDate('');
      loadInventory();
      loadListing();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Renewal failed');
    }
  };

  const thumb = listing?.imagePath ? resolvePublicFileUrl(listing.imagePath) : null;
  const tabListing = listing?.listingType === 'machinery' ? 'equipment' : 'real-estate';

  if (!Number.isFinite(listingId) || listingId < 1) {
    return (
      <div className="container py-5">
        <p className="text-danger">Invalid listing.</p>
        <Link to="/infrastructure-leasing/my-properties">Back</Link>
      </div>
    );
  }

  return (
    <div className="container-fluid ile-seller-inv ile-seller-inv--hub py-4">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <Link to="/dashboard/seller">Seller dashboard</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/infrastructure-leasing/my-properties">My properties</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/infrastructure-leasing/my-properties/inventory-overview">Inventory operations</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Listing #{listingId}
          </li>
        </ol>
      </nav>

      <button type="button" className="btn btn-link px-0 mb-2 text-decoration-none" onClick={() => navigate('/infrastructure-leasing/my-properties')}>
        <FaArrowLeft className="me-1" /> Back to my properties
      </button>

      <div className="ile-seller-inv-head">
        <div className="d-flex flex-wrap gap-3 align-items-start justify-content-between">
          <div className="d-flex gap-3 align-items-start">
            {thumb ? (
              <img src={thumb} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }} />
            ) : (
              <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ width: 72, height: 72 }}>
                {listing?.listingType === 'machinery' ? <FaIndustry /> : <FaBuilding />}
              </div>
            )}
            <div>
              <h1 className="h4 mb-1">{listing?.title || `Listing ${listingId}`}</h1>
              <div className="small text-muted">{listing?.locationLine}</div>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <Link className="btn btn-sm btn-outline-dark" to={`/infrastructure-leasing/listing/${listingId}?tab=${tabListing}`} target="_blank" rel="noopener noreferrer">
                  Public view <FaExternalLinkAlt size={11} className="ms-1" />
                </Link>
                <Link className="btn btn-sm btn-outline-secondary" to={`/infrastructure-leasing/my-properties/${listingId}/enquiries`}>
                  Enquiries
                </Link>
              </div>
            </div>
          </div>
          <button type="button" className="btn btn-sm btn-dark" onClick={() => loadInventory()}>
            Refresh data
          </button>
        </div>
      </div>

      <div className="d-flex ile-seller-inv-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={tab === t.id ? 'active' : ''}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loadErr && !loading && (
        <div className="alert alert-danger">{loadErr}</div>
      )}

      {loading && <p className="text-muted">Loading inventory…</p>}

      {!loading && invPayload && tab === 'overview' && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="ile-seller-inv-kpi">
                <div className="ile-seller-inv-kpi__label">Listing availability state</div>
                <div className="ile-seller-inv-kpi__value" style={{ fontSize: '1.05rem' }}>
                  {availability?.listingAvailabilityState?.replace(/_/g, ' ') || '—'}
                </div>
                {headline && (
                  <div className="ile-seller-inv-kpi__meta">{headline.headline}{headline.sub ? ` — ${headline.sub}` : ''}</div>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <div className="ile-seller-inv-kpi">
                <div className="ile-seller-inv-kpi__label">Inventory lines</div>
                <div className="ile-seller-inv-kpi__value">{items.length}</div>
                <div className="ile-seller-inv-kpi__meta">
                  {availability?.inventoryManaged ? 'Tracked' : 'Not tracked — add lines in Configure'}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="ile-seller-inv-kpi">
                <div className="ile-seller-inv-kpi__label">Active blocks</div>
                <div className="ile-seller-inv-kpi__value">{blocks.length}</div>
                <div className="ile-seller-inv-kpi__meta">Maintenance / holds reducing availability</div>
              </div>
            </div>
          </div>

          <div className="ile-seller-inv-panel">
            <h3>Effective availability (summary)</h3>
            {lineBullets.length ? (
              <ul className="mb-0">
                {lineBullets.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            ) : (
              <p className="text-muted small mb-0">No inventory dimensions on file.</p>
            )}
          </div>

          <div className="ile-seller-inv-panel">
            <h3>Booked ranges (merged)</h3>
            <ul className="mb-0 small">
              {formatBookedRangesSummaryLines(availability?.bookedRanges || [], 12).map((l) => (
                <li key={l}>{l}</li>
              ))}
              {(availability?.bookedRanges || []).length === 0 && <li className="text-muted">None</li>}
            </ul>
            {estimateEarliestCapacityAfterBookings(availability?.bookedRanges || []) && (
              <p className="small text-muted mt-2 mb-0">
                Indicative capacity reopening after last window:{' '}
                <strong>{estimateEarliestCapacityAfterBookings(availability?.bookedRanges || [])}</strong>
              </p>
            )}
          </div>

          <div className="ile-seller-inv-panel">
            <h3>Allocations snapshot</h3>
            <div className="table-responsive">
              <table className="table table-sm mb-0 ile-seller-inv-table">
                <thead>
                  <tr>
                    <th>Enquiry</th>
                    <th>Party</th>
                    <th>Status</th>
                    <th>Window</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.filter((a) => ['reserved', 'confirmed'].includes(String(a.allocation_status).toLowerCase())).slice(0, 8).map((a) => (
                    <tr key={a.infrastructure_inventory_allocation_id}>
                      <td>#{a.infrastructure_listing_enquiry_id}</td>
                      <td className="small">{[str(a.contact_name), str(a.contact_company)].filter(Boolean).join(' · ') || '—'}</td>
                      <td>{a.allocation_status}</td>
                      <td className="small text-nowrap">
                        {isoFromSql(a.lease_start_date)} → {isoFromSql(a.lease_end_date)}
                      </td>
                    </tr>
                  ))}
                  {allocations.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-muted small">No allocation rows.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && invPayload && tab === 'configure' && (
        <>
          <div className="alert alert-secondary small py-2">
            <strong>Inventory kind</strong> is fixed after creation (backend). Use <strong>Add line</strong> for a different model. Partial leasing metadata for hybrid lives in JSON.
          </div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold">Inventory lines</span>
            <button type="button" className="btn btn-sm btn-dark" onClick={() => setAddOpen(true)}>
              Add inventory line
            </button>
          </div>
          <div className="table-responsive border rounded bg-white">
            <table className="table table-sm table-hover align-middle mb-0 ile-seller-inv-table">
              <thead className="table-light">
                <tr>
                  <th>Kind</th>
                  <th>Label</th>
                  <th>Totals</th>
                  <th>Scope</th>
                  <th>Default</th>
                  <th>Active</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.infrastructure_listing_inventory_id}>
                    <td className="text-uppercase small">{row.inventory_kind}</td>
                    <td>{row.label || '—'}</td>
                    <td className="small">
                      {[row.total_units != null ? `${row.total_units} u` : null, row.total_area != null ? `${row.total_area} ${row.area_uom}` : null, row.total_capacity != null ? `${row.total_capacity} ${row.capacity_uom || ''}` : null].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="small">{row.transaction_scope}</td>
                    <td>{Number(row.is_default) === 1 ? 'Yes' : 'No'}</td>
                    <td>{Number(row.is_active) === 1 ? 'Yes' : 'No'}</td>
                    <td className="text-end text-nowrap">
                      <button type="button" className="btn btn-sm btn-outline-dark me-1" onClick={() => openEdit(row)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          setBlockInvId(row.infrastructure_listing_inventory_id);
                          setBlockForm({
                            blockKind: 'maintenance',
                            blockStartDate: '',
                            blockEndDate: '',
                            notes: '',
                            unitsReduction: '',
                            areaReduction: '',
                            capacityReduction: '',
                          });
                          setBlockOpen(true);
                        }}
                      >
                        Add block
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-muted py-4">
                      No inventory lines. Add a line to enable tracked availability.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="ile-seller-inv-panel mt-3">
            <h3>Active maintenance / holds</h3>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Kind</th>
                    <th>Period</th>
                    <th>Notes</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {blocks.map((b) => (
                    <tr key={b.infrastructure_inventory_block_id}>
                      <td className="small">{invLabel(items, b.infrastructure_listing_inventory_id)}</td>
                      <td>{b.block_kind}</td>
                      <td className="small text-nowrap">{isoFromSql(b.block_start_date)} → {isoFromSql(b.block_end_date)}</td>
                      <td className="small">{str(b.notes) || '—'}</td>
                      <td className="text-end">
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deactivateBlock(b.infrastructure_inventory_block_id)}>
                          Deactivate
                        </button>
                      </td>
                    </tr>
                  ))}
                  {blocks.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-muted small">No active blocks.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && invPayload && tab === 'planner' && (
        <div className="ile-seller-inv-panel">
          <h3>Planner</h3>
          <p className="small text-muted mb-3">
            Combined view of merged booked windows (allocations + agreements) and active blocks. Use Configure to add or remove holds.
          </p>
          {plannerRows.map((r, i) => (
            <div key={`${r.kind}-${r.start}-${r.end}-${i}`} className="ile-seller-inv-planner-row">
              <span className="badge bg-dark text-uppercase" style={{ fontSize: '0.65rem' }}>{r.kind}</span>
              <span className="small text-nowrap">{r.start}</span>
              <span>{r.label}{r.notes ? ` — ${r.notes}` : ''}</span>
              <span className="small text-muted text-end">{r.end}</span>
            </div>
          ))}
          {plannerRows.length === 0 && <p className="text-muted small mb-0">Nothing scheduled.</p>}
        </div>
      )}

      {!loading && invPayload && tab === 'leases' && (
        <div className="table-responsive border rounded bg-white">
          <table className="table table-sm table-hover align-middle mb-0 ile-seller-inv-table">
            <thead className="table-light">
              <tr>
                <th>Enquiry</th>
                <th>Party</th>
                <th>Status</th>
                <th>Units / area / cap</th>
                <th>Lease window</th>
                <th>Lease activated</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((a) => {
                const st = String(a.allocation_status).toLowerCase();
                const canRelease = st === 'reserved';
                const leaseAt = a.buyer_lease_activated_at ? String(a.buyer_lease_activated_at).slice(0, 16).replace('T', ' ') : '—';
                return (
                  <tr key={a.infrastructure_inventory_allocation_id}>
                    <td>#{a.infrastructure_listing_enquiry_id}</td>
                    <td className="small">{[str(a.contact_name), str(a.contact_company)].filter(Boolean).join(' · ') || '—'}</td>
                    <td><span className="badge text-bg-secondary">{a.allocation_status}</span></td>
                    <td className="small">
                      {[a.allocated_units != null ? `${a.allocated_units} u` : null, a.allocated_area != null ? `${a.allocated_area} area` : null, a.allocated_capacity != null ? `${a.allocated_capacity} cap` : null].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="small text-nowrap">{isoFromSql(a.lease_start_date)} → {isoFromSql(a.lease_end_date)}</td>
                    <td className="small">{leaseAt}</td>
                    <td className="text-end text-nowrap">
                      <Link
                        to={`/infrastructure-leasing/my-properties/${listingId}/enquiries/${a.infrastructure_listing_enquiry_id}/agreement`}
                        className="btn btn-sm btn-outline-dark me-1"
                      >
                        Agreement
                      </Link>
                      {canRelease ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setReleaseModal({ open: true, enquiryId: a.infrastructure_listing_enquiry_id })}
                        >
                          Release reserved
                        </button>
                      ) : (
                        <span className="small text-muted">Release only if reserved</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {allocations.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-muted py-4">No allocations.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && invPayload && tab === 'renewals' && (
        <>
          <div className="alert alert-light border small mb-3">
            Renewals apply to <strong>confirmed</strong> allocations only (operational lease). Use the date picker — new end must be after the current lease end.
          </div>
          <div className="table-responsive border rounded bg-white">
            <table className="table table-sm table-hover align-middle mb-0 ile-seller-inv-table">
              <thead className="table-light">
                <tr>
                  <th>Enquiry</th>
                  <th>Party</th>
                  <th>Status</th>
                  <th>Ends</th>
                  <th>Within 90d</th>
                  <th className="text-end">Renew</th>
                </tr>
              </thead>
              <tbody>
                {allocations.filter((a) => ['reserved', 'confirmed'].includes(String(a.allocation_status).toLowerCase())).map((a) => {
                  const st = String(a.allocation_status).toLowerCase();
                  const end = isoFromSql(a.lease_end_date);
                  const soon = renewalsSoon.some((x) => Number(x.infrastructure_inventory_allocation_id) === Number(a.infrastructure_inventory_allocation_id));
                  return (
                    <tr key={a.infrastructure_inventory_allocation_id}>
                      <td>#{a.infrastructure_listing_enquiry_id}</td>
                      <td className="small">{[str(a.contact_name), str(a.contact_company)].filter(Boolean).join(' · ') || '—'}</td>
                      <td>{a.allocation_status}</td>
                      <td>{end}</td>
                      <td>{soon ? <span className="badge text-bg-warning text-dark">Soon</span> : '—'}</td>
                      <td className="text-end">
                        {st === 'confirmed' ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-dark"
                            onClick={() => {
                              setRenewModal({ open: true, enquiryId: a.infrastructure_listing_enquiry_id, currentEnd: end });
                              setRenewDate('');
                            }}
                          >
                            Extend
                          </button>
                        ) : (
                          <span className="small text-muted">Confirm lease first</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal title="Edit inventory line" open={editOpen} onCancel={() => setEditOpen(false)} onOk={saveEdit} okText="Save">
        <div className="small text-muted mb-2">
          Kind: <strong className="text-dark">{editRow ? editRow.inventory_kind : ''}</strong> (read-only)
        </div>
        <div className="mb-2">
          <label className="form-label small">Label</label>
          <input className="form-control form-control-sm" value={editForm.label} onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))} />
        </div>
        <div className="row g-2">
          <div className="col-4">
            <label className="form-label small">Total units</label>
            <input className="form-control form-control-sm" value={editForm.totalUnits} onChange={(e) => setEditForm((f) => ({ ...f, totalUnits: e.target.value }))} />
          </div>
          <div className="col-4">
            <label className="form-label small">Total area</label>
            <input className="form-control form-control-sm" value={editForm.totalArea} onChange={(e) => setEditForm((f) => ({ ...f, totalArea: e.target.value }))} />
          </div>
          <div className="col-4">
            <label className="form-label small">Total capacity</label>
            <input className="form-control form-control-sm" value={editForm.totalCapacity} onChange={(e) => setEditForm((f) => ({ ...f, totalCapacity: e.target.value }))} />
          </div>
        </div>
        <div className="row g-2 mt-1">
          <div className="col-6">
            <label className="form-label small">Area UoM</label>
            <input className="form-control form-control-sm" value={editForm.areaUom} onChange={(e) => setEditForm((f) => ({ ...f, areaUom: e.target.value }))} />
          </div>
          <div className="col-6">
            <label className="form-label small">Capacity UoM</label>
            <input className="form-control form-control-sm" value={editForm.capacityUom} onChange={(e) => setEditForm((f) => ({ ...f, capacityUom: e.target.value }))} />
          </div>
        </div>
        <div className="mt-2">
          <label className="form-label small">Transaction scope</label>
          <select className="form-select form-select-sm" value={editForm.transactionScope} onChange={(e) => setEditForm((f) => ({ ...f, transactionScope: e.target.value }))}>
            <option value="lease">lease</option>
            <option value="sale">sale</option>
            <option value="both">both</option>
          </select>
        </div>
        <div className="form-check mt-2">
          <input className="form-check-input" type="checkbox" checked={editForm.isDefault} id="edef" onChange={(e) => setEditForm((f) => ({ ...f, isDefault: e.target.checked }))} />
          <label className="form-check-label small" htmlFor="edef">Default line for allocations</label>
        </div>
        <div className="form-check">
          <input className="form-check-input" type="checkbox" checked={editForm.isActive} id="eact" onChange={(e) => setEditForm((f) => ({ ...f, isActive: e.target.checked }))} />
          <label className="form-check-label small" htmlFor="eact">Active (soft-off tracking when unchecked)</label>
        </div>
        {editRow && str(editRow.inventory_kind).toLowerCase() === 'hybrid' && (
          <div className="mt-2">
            <label className="form-label small">Hybrid dimensions (JSON)</label>
            <textarea className="form-control form-control-sm font-monospace" rows={4} value={editForm.hybridJson} onChange={(e) => setEditForm((f) => ({ ...f, hybridJson: e.target.value }))} />
          </div>
        )}
      </Modal>

      <Modal title="Add inventory line" open={addOpen} onCancel={() => setAddOpen(false)} onOk={submitAddLine} okText="Create">
        <div className="mb-2">
          <label className="form-label small">Kind</label>
          <select className="form-select form-select-sm" value={addForm.inventoryKind} onChange={(e) => setAddForm((f) => ({ ...f, inventoryKind: e.target.value }))}>
            {KIND_OPTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="mb-2">
          <label className="form-label small">Label</label>
          <input className="form-control form-control-sm" value={addForm.label} onChange={(e) => setAddForm((f) => ({ ...f, label: e.target.value }))} />
        </div>
        {(addForm.inventoryKind === 'unit' || addForm.inventoryKind === 'hybrid') && (
          <div className="mb-2">
            <label className="form-label small">Total units</label>
            <input type="number" className="form-control form-control-sm" value={addForm.totalUnits} onChange={(e) => setAddForm((f) => ({ ...f, totalUnits: e.target.value }))} />
          </div>
        )}
        {(addForm.inventoryKind === 'area' || addForm.inventoryKind === 'time' || addForm.inventoryKind === 'hybrid') && (
          <div className="mb-2">
            <label className="form-label small">Total area</label>
            <input type="number" className="form-control form-control-sm" value={addForm.totalArea} onChange={(e) => setAddForm((f) => ({ ...f, totalArea: e.target.value }))} />
          </div>
        )}
        {(addForm.inventoryKind === 'capacity' || addForm.inventoryKind === 'hybrid') && (
          <>
            <div className="mb-2">
              <label className="form-label small">Total capacity</label>
              <input type="number" className="form-control form-control-sm" value={addForm.totalCapacity} onChange={(e) => setAddForm((f) => ({ ...f, totalCapacity: e.target.value }))} />
            </div>
            <div className="mb-2">
              <label className="form-label small">Capacity UoM</label>
              <input className="form-control form-control-sm" value={addForm.capacityUom} onChange={(e) => setAddForm((f) => ({ ...f, capacityUom: e.target.value }))} />
            </div>
          </>
        )}
        {addForm.inventoryKind === 'hybrid' && (
          <div className="mb-2">
            <label className="form-label small">Note (stored in hybrid JSON)</label>
            <input className="form-control form-control-sm" value={addForm.hybridNote} onChange={(e) => setAddForm((f) => ({ ...f, hybridNote: e.target.value }))} />
          </div>
        )}
        <div className="mb-2">
          <label className="form-label small">Area UoM</label>
          <input className="form-control form-control-sm" value={addForm.areaUom} onChange={(e) => setAddForm((f) => ({ ...f, areaUom: e.target.value }))} />
        </div>
      </Modal>

      <Modal title="Add hold / maintenance block" open={blockOpen} onCancel={() => setBlockOpen(false)} onOk={submitBlock} okText="Save block">
        <div className="mb-2">
          <label className="form-label small">Kind</label>
          <select className="form-select form-select-sm" value={blockForm.blockKind} onChange={(e) => setBlockForm((f) => ({ ...f, blockKind: e.target.value }))}>
            <option value="maintenance">maintenance</option>
            <option value="owner_hold">owner_hold</option>
            <option value="other">other</option>
          </select>
        </div>
        <div className="row g-2">
          <div className="col-6">
            <label className="form-label small">Start</label>
            <input type="date" className="form-control form-control-sm" value={blockForm.blockStartDate} onChange={(e) => setBlockForm((f) => ({ ...f, blockStartDate: e.target.value }))} />
          </div>
          <div className="col-6">
            <label className="form-label small">End</label>
            <input type="date" className="form-control form-control-sm" value={blockForm.blockEndDate} onChange={(e) => setBlockForm((f) => ({ ...f, blockEndDate: e.target.value }))} />
          </div>
        </div>
        <div className="mt-2">
          <label className="form-label small">Notes</label>
          <textarea className="form-control form-control-sm" rows={2} value={blockForm.notes} onChange={(e) => setBlockForm((f) => ({ ...f, notes: e.target.value }))} />
        </div>
        <p className="small text-muted mt-2 mb-0">Optional partial reductions (leave blank for full-line impact on that dimension).</p>
        <div className="row g-2 mt-1">
          <div className="col-4">
            <label className="form-label small">Units Δ</label>
            <input className="form-control form-control-sm" value={blockForm.unitsReduction} onChange={(e) => setBlockForm((f) => ({ ...f, unitsReduction: e.target.value }))} />
          </div>
          <div className="col-4">
            <label className="form-label small">Area Δ</label>
            <input className="form-control form-control-sm" value={blockForm.areaReduction} onChange={(e) => setBlockForm((f) => ({ ...f, areaReduction: e.target.value }))} />
          </div>
          <div className="col-4">
            <label className="form-label small">Cap Δ</label>
            <input className="form-control form-control-sm" value={blockForm.capacityReduction} onChange={(e) => setBlockForm((f) => ({ ...f, capacityReduction: e.target.value }))} />
          </div>
        </div>
      </Modal>

      <Modal
        title="Release reserved allocation?"
        open={releaseModal.open}
        onCancel={() => setReleaseModal({ open: false, enquiryId: null })}
        onOk={doRelease}
        okText="Release"
        okButtonProps={{ danger: true }}
      >
        <p className="small">
          Enquiry #{releaseModal.enquiryId}. If the deal is still <strong>awaiting seller signature</strong>, release
          cancels the <strong>whole pending deal</strong> (agreement + enquiry + inventory). If it is{' '}
          <strong>accepted</strong> before buyer sign, release runs a <strong>coordinated cancel</strong>. If the buyer has
          signed but is still on <strong>payment_due</strong> without payment, release may run the <strong>Phase 11</strong>{' '}
          coordinated cancel. Otherwise only the reserved allocation row is released (commercial state on the enquiry may
          remain — use Enquiries when a full cancel is available).
        </p>
        <label className="form-label small">Reason (optional)</label>
        <input className="form-control form-control-sm" value={releaseReason} onChange={(e) => setReleaseReason(e.target.value)} />
      </Modal>

      <Modal title="Extend lease end" open={renewModal.open} onCancel={() => setRenewModal({ open: false, enquiryId: null, currentEnd: '' })} onOk={doRenew} okText="Save renewal">
        <p className="small">Enquiry #{renewModal.enquiryId}. Current end: <strong>{renewModal.currentEnd}</strong></p>
        <label className="form-label small">New lease end (YYYY-MM-DD)</label>
        <input type="date" className="form-control form-control-sm" value={renewDate} onChange={(e) => setRenewDate(e.target.value)} />
      </Modal>
    </div>
  );
}
